<template>
  <div>
    <modal-template ref="modal" modal-size="2xl">
      <template v-slot:title>
        {{ lang.modals.add_direct_debit_account.headline }}
      </template>
      <template v-slot:default>
        <div class="md:grid md:grid-cols-2 md:gap-4 text-left">
          <div class="space-y-2">
            <!-- Account owner -->
            <input-text
              :label="lang.labels.account_owner"
              :required="true"
              name="name"
              id="name"
              placeholder=""
              v-model="debitAccount.name"
              :error="formErrors.has('name')"
            ></input-text>

            <!-- Street & No -->
            <div class="grid grid-cols-12 gap-4">
              <div class="col-span-8">
                <input-text
                  :label="lang.labels.street"
                  :required="true"
                  name="street"
                  id="street"
                  placeholder=""
                  v-model="debitAccount.street"
                  :error="formErrors.has('street')"
                ></input-text>
              </div>
              <div class="col-span-4">
                <input-text
                  :label="lang.labels.house_number"
                  :required="true"
                  name="house_number"
                  id="house_number"
                  placeholder=""
                  v-model="debitAccount.house_number"
                  :error="formErrors.has('house_number')"
                ></input-text>
              </div>
            </div>

            <!-- Zip & City -->
            <div class="grid grid-cols-12 gap-4">
              <div class="col-span-4">
                <input-text
                  :label="lang.labels.zip"
                  :required="true"
                  name="zip"
                  id="zip"
                  placeholder=""
                  v-model="debitAccount.zip"
                  :error="formErrors.has('zip')"
                ></input-text>
              </div>
              <div class="col-span-8">
                <input-text
                  :label="lang.labels.city"
                  :required="true"
                  name="city"
                  id="city"
                  placeholder=""
                  v-model="debitAccount.city"
                  :error="formErrors.has('city')"
                ></input-text>
              </div>
            </div>
          </div>
          <div class="space-y-2">
            <!-- IBAN -->
            <input-text
              :label="lang.labels.iban"
              :required="true"
              name="iban"
              id="iban"
              placeholder=""
              v-model="debitAccount.iban"
              :error="formErrors.has('iban')"
            ></input-text>

            <!-- BIC -->
            <input-text
              :label="lang.labels.bic"
              :required="true"
              name="bic"
              id="bic"
              placeholder=""
              v-model="debitAccount.bic"
              :error="formErrors.has('bic')"
            ></input-text>

            <!-- Bank -->
            <input-text
              :label="lang.labels.bank"
              :required="true"
              name="bank"
              id="bank"
              placeholder=""
              v-model="debitAccount.bank"
              :error="formErrors.has('bank')"
            ></input-text>
          </div>
        </div>
      </template>
      <template v-slot:footer="footerSlotProps">
        <div
          class="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:space-x-4"
        >
          <plain-button color="default" @click="hideModal()">
            {{ lang.labels.cancel }}
          </plain-button>

          <plain-button color="primary" @click="storeDebitAccountData()"
            >{{ lang.labels.save }}
          </plain-button>
        </div>
      </template>
    </modal-template>
  </div>
</template>

<script setup>
import { ref, reactive } from "vue";
import FormErrors from "./../../FormErrors.js";
import ModalTemplate from "./ModalTemplate.vue";

// Props
const props = defineProps({
  lang: Object,
  modalData: Object,
});

// Template refs
const modal = ref(null);

// Reactive state
const debitAccount = reactive({
  name: "",
  street: "",
  house_number: "",
  zip: "",
  city: "",
  iban: "",
  iban_masked: "",
  bic: "",
  bank: "",
});

const disableButtons = ref(false);
const shakeOnError = ref(true);
const formErrors = reactive(new FormErrors());
const initSetUpDone = ref(false);

// Methods
const showModal = () => {
  modal.value.showModal();
  init();
};

const hideModal = () => {
  modal.value.hideModal();
};

/**
 * initialize function when modal is being loaded
 */
const init = () => {
  if (!initSetUpDone.value) {
    loadDirectDebitData();
    initSetUpDone.value = true;
  }
};

/**
 * load users stored direct debit data - if exist
 */
const loadDirectDebitData = () => {
  axios
    .get(`/web-api/debit-account`)
    .then((response) => {
      let debitAccountData = response.data.debitAccount;

      if (debitAccountData !== "" && debitAccountData !== null) {
        Object.assign(debitAccount, debitAccountData);
      }
    })
    .catch(() => {
      // error => console.log(error)
    });
};

/**
 * Validate - check mandatory fields
 */
const validateForm = () => {
  formErrors.clear();
  shakeOnError.value = true;
  let errors = {};

  // name
  if (debitAccount.name == "" || debitAccount.name == null)
    errors.name = "missing";

  // street
  if (debitAccount.street == "" || debitAccount.street == null)
    errors.street = "missing";

  // house_number
  if (debitAccount.house_number == "" || debitAccount.house_number == null)
    errors.house_number = "missing";

  // zip
  if (debitAccount.zip == "" || debitAccount.zip == null)
    errors.zip = "missing";

  // city
  if (debitAccount.city == "" || debitAccount.city == null)
    errors.city = "missing";

  // iban
  if (debitAccount.iban == "" || debitAccount.iban == null)
    errors.iban = "missing";

  // bic
  if (debitAccount.bic == "" || debitAccount.bic == null)
    errors.bic = "missing";

  // bank
  if (debitAccount.bank == "" || debitAccount.bank == null)
    errors.bank = "missing";

  formErrors.record(errors);

  if (formErrors.any()) {
    setTimeout(() => {
      shakeOnError.value = false;
    }, 500);

    return false;
  }

  return true;
};

/**
 * store new/update direct debit data
 */
const storeDebitAccountData = () => {
  console.log("storeDebitAccountData");
  disableButtons.value = true;

  let validation = validateForm();
  console.log("storeDebitAccountData 2", validation);

  if (validation) {
    axios
      .post(`/web-api/debit-account`, debitAccount)
      .then((response) => {
        location.reload();
      })
      .catch(() => {
        // error => console.log(error)
      });
  } else {
    setTimeout(() => {
      disableButtons.value = false;
    }, 500);
  }
};

// Expose methods for parent components
defineExpose({
  showModal,
  hideModal,
});
</script>
